import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  History,
  IndianRupee,
  Layers,
  LockKeyhole,
  PackageCheck,
  Search,
  ShieldAlert,
  ShieldCheck,
  X,
} from "lucide-react";
import { openShopifyApp } from "../../lib/shopifyApp";
import CategoryLinks from "@/components/CategoryLinks";
import "./HomePage.css";
import { getBrands, getFeaturedBrands, trackBrandSearchSelect } from "@/lib/api";

const HINT_INTERVAL_MS = 2800;

// Same commitments as the Why Trust Us page; keep the wording in step with TRUST_TERMS in TrustIssue.jsx.
const integrityTerms = [
  ["Verification is free", "Every brand is checked at no cost, so there's no fee for us to waive."],
  [
    "Paid for views, never for results",
    "Brands pay only when their badge is shown more than 50,000 times a month. Good numbers or bad, the price is the same.",
  ],
  [
    "Rules, not favours",
    "Thresholds are published and applied by the system. No one on our team can edit a number, hide a review or pause a suspension.",
  ],
];
const badgeProof = ["Company details", "On-time delivery", "Returns and COD"];
const abVariants = [
  { group: "Group A", caption: "sees the badge", benefit: "Proof sits right beside the buy button.", badge: true },
  {
    group: "Group B",
    caption: "sees the same store without it",
    benefit: "Shoppers have to take your word for it.",
    badge: false,
  },
];
const problemHints = [
  [
    History,
    "The brand with no history",
    "A brand-new business can look as polished as one that's been around for years. The design, the photos and the checkout can all look the same. But the storefront won't tell you who runs it or how long it's been operating, and that's often what you most want to know before paying.",
  ],
  [
    IndianRupee,
    "The return that costs more",
    '"Easy returns" can mean different things. Return shipping, restocking fees or "exchange only" rules can make a return cost more than you expected. Sometimes the terms are there, but buried deep enough that most people find out only after they\'ve paid.',
  ],
  [
    Layers,
    "Everything, in different places",
    "What's worth knowing about a brand is spread out: a company registry, a policy page buried in the footer, reviews on other sites, a few comments on social media. Putting it together takes real effort, and most people never have the time to do it before they buy.",
  ],
  [
    ShieldAlert,
    "Claims nobody checked",
    '"Certified", "trusted" and "verified" cost nothing to write. Most of what a brand says about itself is self-reported, and no one outside the brand has looked at it. Without an independent check, you\'re left taking its word for it, and no one tells you when it\'s wrong.',
  ],
];
const evidence = [
  {
    field: "Verified identity",
    source: "Company registry match",
    example: "Bela Consumer Labs Pvt Ltd",
    extraction: "The registered entity that operates the store, matched to the company register by CIN.",
    cadence: "Monthly",
  },
  {
    field: "Date of incorporation",
    source: "Official company record",
    example: "12 March 2021",
    extraction: "Read from the company register, not from an About Us page.",
    cadence: "Monthly",
  },
  {
    field: "Domain age",
    source: "Independent registration record",
    example: "5 years 2 months",
    extraction: "Taken from the domain's registration record at the registrar, independent of anything on the site.",
    cadence: "Monthly",
  },
  {
    field: "COD and charges",
    source: "Live checkout test",
    example: "Yes, ₹49",
    extraction: "Read from the live checkout and re-tested automatically, not from the FAQ page.",
    cadence: "Weekly",
  },
  {
    field: "Returns and refunds",
    source: "Versioned policy snapshot",
    example: "10 days, free pickup",
    extraction: "Snapshot of the live policy, with every previous version kept.",
    cadence: "Weekly",
  },
];
function StatusMark({ available, label }) {
  return (
    <span className={`home-status-mark ${available ? "is-available" : "is-unavailable"}`}>
      {available ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
      <span className="home-visually-hidden">
        {label}: {available ? "available" : "not available"}
      </span>
    </span>
  );
}

// Shared row order: the checks a tracked brand passes come first, so every card lists
// its ticks above its crosses and the rows line up across all three cards.
const STATUS_ROWS = [
  "Full brand page on Rivyou",
  "Date of incorporation",
  "Domain age",
  "COD availability and charges",
  "Returns and refund terms",
  "On-time delivery rate",
  "Payment success rate",
  "Delivery time to your pincode",
  "Reviews locked by Rivyou",
  "Badge live on their own website",
];
const TRACKED_PASSES = 5;
const statusRows = (passed) => STATUS_ROWS.map((label, index) => [label, index < passed]);

const STATUS_CARDS = [
  {
    tone: "verified",
    Icon: ShieldCheck,
    kicker: "Badge verified",
    title: "Connected store, all checks passed",
    copy: "The brand connected its store with read-only access, so Rivyou computes performance itself.",
    rows: statusRows(STATUS_ROWS.length),
  },
  {
    tone: "tracked",
    Icon: PackageCheck,
    kicker: "Tracked, not verified",
    title: "Public record only, no store connected",
    copy: "Rivyou can confirm who they are from public sources, but nobody can confirm how they perform.",
    rows: statusRows(TRACKED_PASSES),
  },
  {
    tone: "blank",
    Icon: CircleHelp,
    kicker: "Not on Rivyou",
    title: "We have no record of this brand",
    copy: "Not an accusation and not a warning. It means we have nothing checked. You can request a check or ask the brand to verify.",
    rows: statusRows(0),
  },
];

function StatusCards() {
  return (
    <div className="home-status-cards">
      {STATUS_CARDS.map(({ tone, Icon, kicker, title, copy, rows }) => (
        <article className={`home-status-pane home-status-pane--${tone}`} key={tone}>
          <header>
            <span className="home-status-pane-kicker">
              <Icon aria-hidden="true" />
              {kicker}
            </span>
            <h3>{title}</h3>
            <p>{copy}</p>
          </header>
          <ul>
            {rows.map(([label, available]) => (
              <li key={label} className={available ? "is-available" : "is-unavailable"}>
                {available ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
                <span>{label}</span>
                <span className="home-visually-hidden">{available ? " (available)" : " (not available)"}</span>
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

const STATUS_SHORT_LABEL = { verified: "Verified", tracked: "Public record", blank: "No record" };

// Mobile table reads from STATUS_CARDS so it always lists the same points as the desktop cards.
function MobileStatusTable() {
  const fields = STATUS_CARDS[0].rows.map(([label]) => label);
  return (
    <div className="home-status-table-wrap">
      <table className="home-status-table">
        <caption>Checks available for each Rivyou brand status</caption>
        <thead>
          <tr>
            <th scope="col">What we check</th>
            {STATUS_CARDS.map(({ tone, Icon, kicker }) => (
              <th scope="col" className={`home-status-table-${tone}`} key={tone}>
                <Icon aria-hidden="true" />
                <span>{STATUS_SHORT_LABEL[tone]}</span>
                <small>{kicker}</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {fields.map((field, fieldIndex) => (
            <tr key={field}>
              <th scope="row">{field}</th>
              {STATUS_CARDS.map(({ tone, rows }) => (
                <td key={tone} className={`home-status-cell--${tone}`}>
                  <StatusMark available={rows[fieldIndex][1]} label={`${field}, ${tone}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Intro({ eyebrow, title, copy, align = "left", id }) {
  return (
    <div className={`home-intro home-intro--${align}`}>
      <div className="home-eyebrow">{eyebrow}</div>
      <h2 id={id}>{title}</h2>
      {copy && <p>{copy}</p>}
    </div>
  );
}

// Brand logo from the API record, falling back to the initial when it is missing or fails to load.
function BrandResultMark({ brand }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [brand.logoUrl]);
  const initial = brand.monogram || brand.name?.charAt(0);

  if (!brand.logoUrl || failed) return <span className="home-result-mark">{initial}</span>;
  return (
    <span className="home-result-mark home-result-mark--logo">
      <img
        src={brand.logoUrl}
        alt=""
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

function BrandSearch({ onSearch, onNavigate }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const root = useRef(null);
  const [hintIndex, setHintIndex] = useState(0);
  // Brand names the search box cycles through, taken from the live featured list.
  const [hintBrands, setHintBrands] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    const timer = window.setTimeout(async () => {
      try {
        const data = q ? await getBrands({ q, limit: 5 }) : await getFeaturedBrands(4);
        const remoteResults = q ? data.results || [] : data || [];
        if (cancelled) return;
        setResults(remoteResults);
        if (!q)
          setHintBrands(
            remoteResults
              .map((brand) => brand.name)
              .filter(Boolean)
              .slice(0, 8),
          );
      } catch {
        if (!cancelled) setResults([]);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  // Rotate the example brand in the empty search box. Stops while typing or with the dropdown open, and never runs for reduced-motion users.
  useEffect(() => {
    if (query || open || hintBrands.length < 2 || window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setHintIndex((i) => (i + 1) % hintBrands.length);
    }, HINT_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [query, open, hintBrands]);

  useEffect(() => {
    const close = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (!open || !root.current) return undefined;
    const fitResultsInsideHero = () => {
      const hero = root.current.closest(".home-hero");
      if (!hero) return;
      const availableHeight = hero.getBoundingClientRect().bottom - root.current.getBoundingClientRect().bottom - 16;
      root.current.style.setProperty("--home-results-max-height", `${Math.max(148, Math.min(420, availableHeight))}px`);
    };
    fitResultsInsideHero();
    window.addEventListener("resize", fitResultsInsideHero);
    return () => window.removeEventListener("resize", fitResultsInsideHero);
  }, [open]);

  const submit = (value = query) => {
    const q = value.trim();
    if (q) {
      setOpen(false);
      onSearch?.(q);
    }
  };
  const select = (brand) => {
    setQuery(brand.name);
    setOpen(false);
    if (brand.id) trackBrandSearchSelect(brand.id, query, "home_redesign");
    const path = `/brands/${encodeURIComponent(brand.id)}`;
    if (onNavigate) onNavigate(path);
    else onSearch?.(brand.name);
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape") return setOpen(false);
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    }
    if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      select(results[active]);
    }
  };

  return (
    <div ref={root} className="home-search-wrap">
      <form
        className="home-search"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        role="search"
      >
        <Search aria-hidden="true" />
        <span className="home-search-field">
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActive(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            enterKeyHint="search"
            aria-label="Search Rivyou for a brand"
            aria-expanded={open}
            aria-controls="home-brand-results"
            role="combobox"
            autoComplete="off"
          />
          {!query && hintBrands.length > 0 && (
            <span className="home-search-hint" aria-hidden="true">
              <span key={hintIndex}>{hintBrands[hintIndex % hintBrands.length]}</span>
            </span>
          )}
        </span>
        {query && (
          <button className="home-search-clear" type="button" onClick={() => setQuery("")} aria-label="Clear search">
            <X />
          </button>
        )}
        <button
          className="home-button home-button--primary home-search-submit"
          type="submit"
          aria-label="Check a brand"
        >
          <span>Check a brand</span>
          <ArrowRight />
        </button>
      </form>
      {open && (
        <div id="home-brand-results" className="home-search-results" role="listbox" aria-label="Brand suggestions">
          <div className="home-search-results-head">
            <span>{query.trim() ? "Matching brands" : "Popular brands"}</span>
            <small>
              {results.length} {results.length === 1 ? "result" : "results"}
            </small>
          </div>
          {results.length > 0 ? (
            results.map((brand, index) => (
              <button
                key={brand.id || brand.name}
                type="button"
                role="option"
                aria-selected={active === index}
                className={active === index ? "is-active" : ""}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(brand)}
              >
                <BrandResultMark brand={brand} />
                <span className="home-result-copy">
                  <strong>{brand.name}</strong>
                  <small>
                    {brand.category || "View brand record"}
                    {brand.location ? ` · ${brand.location}` : ""}
                  </small>
                </span>
                <span className={`home-result-status ${brand.verified ? "is-verified" : "is-unverified"}`}>
                  {brand.verified ? "Verified" : "Not verified"}
                </span>
                <ArrowRight />
              </button>
            ))
          ) : (
            <div className="home-search-results-empty">
              <Search aria-hidden="true" />
              <span>
                <strong>No matching brand yet</strong>
                <small>Check the full directory or request this brand.</small>
              </span>
              <button type="button" onClick={() => submit(query)}>
                View directory <ArrowRight />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HomePage({ onSearch, onNavigate }) {
  return (
    <main className="home-page">
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-container home-hero-grid">
          <div className="home-hero-copy">
            <h1 id="home-title">
              Know the brand
              <br />
              before you <em>buy.</em>
            </h1>
            <p>The details brands bury are the ones that matter. We put them where you can see them before you buy.</p>
            <BrandSearch onSearch={onSearch} onNavigate={onNavigate} />
          </div>
        </div>
        <a className="home-scroll" href="#home-problem-preview" aria-label="Scroll down to see why it matters">
          <ChevronDown />
        </a>
      </section>

      <section
        id="home-problem-preview"
        className="home-section home-problem-preview"
        aria-labelledby="home-problem-preview-title"
      >
        <div className="home-container">
          <div className="home-problem-preview-heading">
            <div className="home-eyebrow">The problem</div>
            <h2 id="home-problem-preview-title">
              Looking legitimate is not the same as <span>being legitimate.</span>
            </h2>
            <p>
              Any brand can build a good-looking website. It can have clean design, confident wording, and a polished
              checkout. But none of that tells you what happened to other customers, what the real terms are, or whether
              the promises are backed by anything. Those answers are usually somewhere else, and most people never have
              time to look. That's the gap we try to close.
            </p>
          </div>
          <div className="home-problem-preview-grid">
            {problemHints.map(([Icon, title, copy]) => (
              <article key={title}>
                <span>
                  <Icon aria-hidden="true" />
                </span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <button
            className="home-button home-button--outline home-problem-preview-link"
            type="button"
            onClick={() => onNavigate?.("/the-badge/the-problem")}
          >
            Read the full breakdown <ArrowRight />
          </button>
        </div>
      </section>

      <section className="home-section home-ledger-section" aria-labelledby="ledger-title">
        <div className="home-container home-ledger-layout">
          <div className="home-ledger-sticky">
            <Intro
              eyebrow="What Rivyou checks"
              id="ledger-title"
              title={
                <>
                  Read the <span>fine print</span> before you order.
                </>
              }
              copy="Return terms, COD charges and company details are usually buried below the product page. We read them for you, check them against real records, and show where each one came from."
            />
            <div className="home-principle">
              <LockKeyhole />
              <span>
                <strong>Proof over polish</strong>If we can't check something, we don't fill it in. We leave it blank.
                Blank means "we couldn't confirm this," never "it's fine."
              </span>
            </div>
          </div>
          <div className="home-ledger" role="table" aria-label="Information Rivyou checks">
            <div className="home-ledger-head" role="row">
              <span role="columnheader">FIELD</span>
              <span role="columnheader">EXAMPLE + HOW WE EXTRACT IT</span>
              <span role="columnheader">CADENCE</span>
            </div>
            {evidence.map(({ field, source, example, extraction, cadence }, index) => (
              <div className="home-ledger-row" role="row" key={field} style={{ "--row-index": index }}>
                <strong role="cell">{field}</strong>
                <div className="home-ledger-source" role="cell">
                  <strong>{example}</strong>
                  <span>{extraction}</span>
                  <small>{source}</small>
                </div>
                <small className="home-ledger-cadence" role="cell">
                  {cadence.toLowerCase()}
                </small>
              </div>
            ))}
            <div className="home-ledger-footer">
              <CheckCircle2 />
              <span>
                <strong>Unverified means blank.</strong> Rivyou does not repeat a brand’s answer as an independent fact.
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-status" aria-labelledby="status-title">
        <div className="home-container">
          <Intro
            eyebrow="Clear status, no grey area"
            id="status-title"
            title={
              <>
                Three kinds of brand. <span>Easy to tell apart.</span>
              </>
            }
            copy="A brand page tells you exactly how much Rivyou can verify and where the evidence stops."
          />
          <StatusCards />
          <MobileStatusTable />
        </div>
      </section>

      <section className="home-section home-integrity" aria-labelledby="integrity-title">
        <div className="home-container">
          <Intro
            eyebrow="How Rivyou stays independent"
            id="integrity-title"
            title={
              <>
                Nobody can pay us <span>to pass.</span>
              </>
            }
          />
          <ul className="home-integrity-terms">
            {integrityTerms.map(([title, copy]) => (
              <li key={title}>
                <h3>{title}</h3>
                <p>{copy}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-section home-brands" aria-labelledby="brands-title">
        <div className="home-container home-brands-grid">
          <div className="home-brands-copy">
            <Intro
              eyebrow="For brands"
              id="brands-title"
              title={
                <>
                  Run an honest store? <span>Let shoppers check.</span>
                </>
              }
              copy="Free verification, a live badge, reviews only real buyers can write, and a built-in A/B test that shows what the badge does for your sales."
            />
            <div className="home-brands-cta">
              <button
                className="home-button home-button--primary home-brands-button"
                type="button"
                onClick={openShopifyApp}
              >
                Get the badge, free <ArrowRight aria-hidden="true" />
              </button>
              <small>Verification is free and takes about three minutes.</small>
            </div>
          </div>
          <figure
            className="home-ab"
            aria-label="The same product page shown twice: one copy with the Verified by Rivyou badge, one without"
          >
            {abVariants.map(({ group, caption, benefit, badge }) => (
              <div className="home-ab-variant" key={group}>
                <div className={`home-ab-card${badge ? " has-badge" : ""}`} aria-hidden="true">
                  <span className="home-ab-image" />
                  <span className="home-ab-line" />
                  <span className="home-ab-line home-ab-line--short" />
                  {badge ? (
                    <div className="home-ab-slot home-ab-slot--badge">
                      <b>Verified by Rivyou</b>
                      <ul>
                        {badgeProof.map((item) => (
                          <li key={item}>
                            <Check />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <span className="home-ab-slot home-ab-slot--empty">No proof beside the button</span>
                  )}
                  <span className={`home-ab-buy ${badge ? "home-ab-buy--on" : "home-ab-buy--idle"}`}>Add to cart</span>
                </div>
                <p>
                  <strong>{group}</strong> {caption}
                  <small>{benefit}</small>
                </p>
              </div>
            ))}
          </figure>
        </div>
      </section>

      <section className="home-section home-browse" aria-labelledby="browse-title">
        <div className="home-container">
          <Intro
            eyebrow="Browse brands"
            id="browse-title"
            title={
              <>
                Find a brand by <span>category.</span>
              </>
            }
            copy="Company details, return terms and contact information for online brands, grouped by what they sell."
          />
          <CategoryLinks className="home-browse-list" linkClassName="home-browse-link" label="Brand categories" />
        </div>
      </section>

      <section className="home-section home-closing" aria-labelledby="closing-title">
        <div className="home-container">
          <Intro
            eyebrow="What you get from Rivyou"
            id="closing-title"
            align="center"
            title={
              <>
                The information you need,
                <br />
                made <span>clear and useful.</span>
              </>
            }
            copy="The facts in one place, in plain words, with where each one came from."
          />
        </div>
      </section>
    </main>
  );
}
